import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { API_BASE } from '../config';
import { useAuth } from '../contexts/AuthContext';
import { useLocale } from '../contexts/LocaleContext';
import { PROJECT_SECTION_ICONS } from '../utils/concept-icons';
import { getDocumentPanelPath } from '../utils/document-panel-paths';
import { takeMostRecent } from '../utils/sort-by-updated-at';
import { ProjectSectionHub } from './ProjectSectionHub';
import { SectionHubCard, type SectionHubItem } from './SectionHubCard';

const RECENT_LIMIT = 5;

interface NamedEntity {
  id: string;
  name: string;
  relevance?: number;
}

interface TimelineListItem {
  id: string;
  name: string;
  updatedAt: string;
}

interface TagOverviewRow {
  tag: string;
  usageCount: number;
}

export const WorldbuildingLanding: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { token } = useAuth();
  const { t } = useLocale();
  const [loading, setLoading] = useState(true);
  const [mainDocumentId, setMainDocumentId] = useState<string | null>(null);
  const [characterEntities, setCharacterEntities] = useState<NamedEntity[]>([]);
  const [locationEntities, setLocationEntities] = useState<NamedEntity[]>([]);
  const [timelineEntityItems, setTimelineEntityItems] = useState<TimelineListItem[]>([]);
  const [tagRows, setTagRows] = useState<TagOverviewRow[]>([]);

  useEffect(() => {
    if (!projectId || !token) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const load = async (): Promise<void> => {
      setLoading(true);
      try {
        const projectRes = await fetch(`${API_BASE}/api/projects/${projectId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const projectData = projectRes.ok ? await projectRes.json() : {};
        const nextMainDocumentId =
          typeof projectData.mainDocumentId === 'string' ? projectData.mainDocumentId : null;

        const authHeaders = { Authorization: `Bearer ${token}` };
        const [charactersRes, locationsRes, timelineRes, tagsRes] = await Promise.all([
          nextMainDocumentId != null
            ? fetch(`${API_BASE}/api/documents/${nextMainDocumentId}/characters`, {
                headers: authHeaders,
              })
            : Promise.resolve(undefined),
          nextMainDocumentId != null
            ? fetch(`${API_BASE}/api/documents/${nextMainDocumentId}/locations`, {
                headers: authHeaders,
              })
            : Promise.resolve(undefined),
          fetch(`${API_BASE}/api/projects/${projectId}/timeline-items`, {
            headers: authHeaders,
          }),
          fetch(`${API_BASE}/api/projects/${projectId}/tags/overview`, {
            headers: authHeaders,
          }),
        ]);

        const charactersData =
          charactersRes?.ok ? await charactersRes.json() : { characters: [] };
        const locationsData =
          locationsRes?.ok ? await locationsRes.json() : { locations: [] };
        const timelineData = timelineRes.ok ? await timelineRes.json() : { items: [] };
        const tagsData = tagsRes.ok ? await tagsRes.json() : { tags: [] };

        if (cancelled) {
          return;
        }

        setMainDocumentId(nextMainDocumentId);
        setCharacterEntities(
          ((charactersData.characters || []) as NamedEntity[])
            .slice()
            .sort((a, b) => (b.relevance ?? 0) - (a.relevance ?? 0))
            .slice(0, RECENT_LIMIT)
        );
        setLocationEntities(
          ((locationsData.locations || []) as NamedEntity[])
            .slice()
            .sort((a, b) => (b.relevance ?? 0) - (a.relevance ?? 0))
            .slice(0, RECENT_LIMIT)
        );
        setTimelineEntityItems(
          takeMostRecent((timelineData.items || []) as TimelineListItem[], RECENT_LIMIT)
        );
        setTagRows(
          ((tagsData.tags || []) as TagOverviewRow[])
            .slice()
            .sort((a, b) => b.usageCount - a.usageCount)
            .slice(0, RECENT_LIMIT)
        );
      } catch {
        if (!cancelled) {
          setMainDocumentId(null);
          setCharacterEntities([]);
          setLocationEntities([]);
          setTimelineEntityItems([]);
          setTagRows([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [projectId, token]);

  const charactersHref =
    projectId && mainDocumentId != null
      ? getDocumentPanelPath(projectId, mainDocumentId, 'characters')
      : `/projects/${projectId}/worldbuilding`;
  const locationsHref =
    projectId && mainDocumentId != null
      ? getDocumentPanelPath(projectId, mainDocumentId, 'locations')
      : `/projects/${projectId}/worldbuilding`;

  const characters: SectionHubItem[] = characterEntities.map((character) => ({
    id: character.id,
    label: character.name || t('untitled'),
    href: `${charactersHref}?characterId=${encodeURIComponent(character.id)}`,
  }));

  const locations: SectionHubItem[] = locationEntities.map((location) => ({
    id: location.id,
    label: location.name || t('untitled'),
    href: `${locationsHref}?locationId=${encodeURIComponent(location.id)}`,
  }));

  const timelineItems: SectionHubItem[] = timelineEntityItems.map((item) => ({
    id: item.id,
    label: item.name || t('untitled'),
    href: `/projects/${projectId}/timeline?timelineItemId=${encodeURIComponent(item.id)}`,
  }));

  const tags: SectionHubItem[] = tagRows.map((row) => ({
    id: row.tag,
    label: row.tag,
    href: `/projects/${projectId}/tags?tag=${encodeURIComponent(row.tag)}`,
  }));

  if (!projectId) {
    return null;
  }

  return (
    <ProjectSectionHub
      title={t('Worldbuilding')}
      titleIcon={PROJECT_SECTION_ICONS.worldbuilding}
      intro={t('worldbuilding_landing_intro')}
      loading={loading}
    >
      <SectionHubCard
        title={t('characters')}
        icon={PROJECT_SECTION_ICONS.characters}
        href={charactersHref}
        emptyDescription={
          mainDocumentId != null
            ? t('characters_empty_description')
            : t('section_hub_needs_main_document')
        }
        items={characters}
        itemsHeading={t('section_hub_top_characters')}
      />
      <SectionHubCard
        title={t('locations')}
        icon={PROJECT_SECTION_ICONS.locations}
        href={locationsHref}
        emptyDescription={
          mainDocumentId != null
            ? t('locations_empty_description')
            : t('section_hub_needs_main_document')
        }
        items={locations}
        itemsHeading={t('section_hub_top_locations')}
      />
      <SectionHubCard
        title={t('Timeline')}
        icon={PROJECT_SECTION_ICONS.timeline}
        href={`/projects/${projectId}/timeline`}
        emptyDescription={t('timeline_empty_description')}
        items={timelineItems}
        itemsHeading={t('section_hub_most_recent_timeline')}
      />
      <SectionHubCard
        title={t('tag_manager_page_title')}
        icon={PROJECT_SECTION_ICONS.tags}
        href={`/projects/${projectId}/tags`}
        emptyDescription={t('worldbuilding_landing_tags_empty')}
        items={tags}
        itemsHeading={t('section_hub_top_tags')}
      />
    </ProjectSectionHub>
  );
};
